import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  Map,
  ListChecks,
  BookOpenCheck,
  Users,
  Layers,
  ScrollText,
  UserCog,
  ClipboardCheck,
  LogOut,
  ChevronRight,
  Sprout,
  Menu,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["agent", "admin", "super_admin"] },
  { to: "/map", label: "Layout Map", icon: Map, roles: ["agent", "admin", "super_admin"] },
  { to: "/inventory", label: "Inventory", icon: ListChecks, roles: ["agent", "admin", "super_admin"] },
  { to: "/bookings", label: "My Bookings", icon: BookOpenCheck, roles: ["agent"] },
  { to: "/admin/bookings", label: "Bookings", icon: BookOpenCheck, roles: ["admin", "super_admin"] },
  { to: "/admin/pending-portions", label: "Pending Portions", icon: ClipboardCheck, roles: ["admin", "super_admin"] },
  { to: "/admin/users", label: "Users", icon: Users, roles: ["admin", "super_admin"] },
  { to: "/admin/my-channel-partners", label: "My Channel Partners", icon: UserCog, roles: ["admin"] },
  { to: "/admin/assignments", label: "CP Assignments", icon: Users, roles: ["super_admin"] },
  { to: "/admin/plots", label: "Manage LPs & Plots", icon: Layers, roles: ["super_admin"] },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, roles: ["admin", "super_admin"] },
  { to: "/profile", label: "My Profile", icon: UserCog, roles: ["admin", "super_admin"] },
];

function NavList({ items, onNavigate }) {
  return (
    <nav className="flex-1 px-3 py-4 space-y-1" data-testid="app-sidebar">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors",
              isActive ? "bg-[#234B36] text-white" : "text-[#1A1F16] hover:bg-[#F0EFEA]"
            )
          }
        >
          <item.icon className="h-4 w-4" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function BrandBlock() {
  return (
    <Link to="/dashboard" className="flex items-center gap-3 px-5 py-5 border-b border-[#E5E2DC]">
      <img src="/images/anuttara_logo.jpg" alt="Anuttara Township" className="h-11 w-11 object-cover rounded-sm flex-shrink-0" />
      <div className="leading-tight min-w-0">
        <div className="font-heading text-[15px] font-semibold truncate">Anuttara</div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653]">Phase 2 · Kamareddy</div>
      </div>
    </Link>
  );
}

function UserBlock({ user, roleLabel, onLogout, testIdPrefix = "" }) {
  return (
    <div className="p-4 border-t border-[#E5E2DC]">
      <div className="text-xs text-[#5C6653] mb-1">Signed in as</div>
      <div className="text-sm font-medium truncate" data-testid={`${testIdPrefix}current-user-name`}>{user?.name}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#C06C47] font-semibold mt-1">{roleLabel}</div>
      <button
        data-testid={`${testIdPrefix}logout-button`}
        onClick={onLogout}
        className="mt-3 w-full flex items-center justify-between text-sm px-3 py-2 border border-[#E5E2DC] hover:bg-[#F0EFEA] transition-colors"
      >
        <span className="flex items-center gap-2"><LogOut className="h-4 w-4" /> Logout</span>
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes(user?.role));
  const roleLabel = { agent: "Channel Partner", admin: "Admin", super_admin: "Super Admin" }[user?.role] || user?.role;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1A1F16] flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-[#E5E2DC] bg-white">
        <BrandBlock />
        <NavList items={items} />
        <UserBlock user={user} roleLabel={roleLabel} onLogout={handleLogout} />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-[#E5E2DC] px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/images/anuttara_logo.jpg" alt="Anuttara" className="h-9 w-9 object-cover rounded-sm" />
          <div className="font-heading font-semibold text-sm">Anuttara</div>
        </div>
        <div className="flex items-center gap-2">
          <img src="/images/citysquare_logo.jpg" alt="City Square" className="h-8 object-contain" data-testid="mobile-citysquare-logo" />
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="h-9 w-9 flex items-center justify-center border border-[#E5E2DC] rounded-sm">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 bg-white">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation menu</SheetTitle>
                <SheetDescription>Access dashboard, layout map, inventory and admin sections</SheetDescription>
              </SheetHeader>
              <div className="flex flex-col h-full">
                <BrandBlock />
                <NavList items={items} onNavigate={() => setDrawerOpen(false)} />
                <UserBlock user={user} roleLabel={roleLabel} onLogout={handleLogout} testIdPrefix="mobile-" />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <main className="flex-1 min-h-screen md:pt-0 pt-14 w-full overflow-x-hidden">
        {children}
      </main>
    </div>
  );
}
